import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { SOLUTIONS } from "@/data/content";
import { useLang } from "@/i18n";
import { SectionLabel } from "./ui";
import { Reveal } from "./Reveal";
import { cn } from "@/lib/utils";

export const Solutions = () => {
  const { t } = useLang();
  const s = t.solutions;
  const [isMobile, setIsMobile] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const visible = isMobile && !expanded ? s.items.slice(0, 6) : s.items;

  return (
    <section id="solutions" data-testid="solutions-section" className="py-16 md:py-24 bg-[#FAFAFC]">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <Reveal className="max-w-2xl">
          <SectionLabel>{s.label}</SectionLabel>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-navy-900 leading-tight">{s.title}</h2>
          <p className="mt-4 text-base md:text-lg text-charcoal-700 leading-relaxed">{s.sub}</p>
        </Reveal>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
          {visible.map((item, i) => {
            const Icon = (SOLUTIONS[i] || SOLUTIONS[SOLUTIONS.length - 1]).icon;
            return (
              <Reveal key={item.title} delay={Math.min(i, 5) * 0.04} className="h-full">
                <div className="group flex items-start gap-4 h-full rounded-2xl bg-white border border-navy-100 p-5 shadow-[0_2px_10px_rgba(6,16,32,0.04)] hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300" data-testid={`solution-card-${i}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy-900 text-gold-400 group-hover:bg-gold-500 group-hover:text-navy-900 transition-colors duration-300">
                    <Icon size={21} strokeWidth={1.8} aria-hidden />
                  </span>
                  <div>
                    <h3 className="font-display text-base font-semibold text-navy-900 leading-snug">{item.title}</h3>
                    <p className="mt-1 text-sm text-charcoal-700 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        {isMobile && s.items.length > 6 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            data-testid="solutions-view-all"
            className="mt-5 mx-auto flex items-center gap-2 rounded-full border border-navy-200 bg-white px-6 py-3 min-h-[44px] text-sm font-semibold text-navy-900 hover:border-gold-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
          >
            {expanded ? s.viewLess : s.viewAll}
            <ChevronDown size={16} className={cn("transition-transform", expanded && "rotate-180")} aria-hidden />
          </button>
        )}
      </div>
    </section>
  );
};
